import React, { useEffect, useRef } from 'react';

import useMountAnimation from '../../../../hooks/useMountAnimation';
import { Slide, type StepSlideDir } from './DesktopStepSlide.styles';

/**
 * Module-level memory of the last desktop step index. Each step keys a fresh mount
 * of this wrapper, so the new instance has no previous state to diff against — the
 * module memory lets it still tell forward (a later step) from back (an earlier
 * step). Lazy `null` start: the FIRST step painted on a fresh load / reload fades
 * in with no horizontal travel, and every step change after that slides
 * directionally. (The mobile sibling baselines at the intro instead — desktop has
 * no intro screen.)
 */
let lastStepIndex: number | null = null;

/** Re-baseline the slide memory (so the next step to mount fades in with no
 *  horizontal travel rather than inheriting a stale direction). The memory is
 *  module-scoped to survive the per-step remounts, which means it also survives an
 *  SPA exit/re-entry of the wizard; call this once on each fresh entry so a new
 *  visit's first step enters neutrally instead of sliding in from the wrong side. */
export const resetDesktopStepSlide = (): void => {
  lastStepIndex = null;
};

/**
 * Slides the active desktop wizard step's body IN on each step change: forward
 * navigations (a later step) arrive from the right, back from the left. Slide-in
 * only — the outgoing step unmounts with its route, matching the mobile flow. The
 * progress sidebar, header and footer live OUTSIDE this wrapper, so they stay fixed
 * while only the step body travels.
 *
 * Mount it with `key={step}` so each step change remounts it and replays the
 * entrance (the chrome around it persists, so only the body slides).
 */
export const DesktopStepSlide = ({
  step,
  children,
}: {
  step: number;
  children: React.ReactNode;
}): React.ReactElement => {
  // Direction is decided once at this mount's first render — before the effect
  // advances the module memory — and frozen for the step's life, so an in-step
  // re-render never replays the entrance.
  const dirRef = useRef<StepSlideDir | null>(null);
  if (dirRef.current === null) {
    dirRef.current =
      lastStepIndex === null || step === lastStepIndex ? 'none' : step > lastStepIndex ? 'forward' : 'back';
  }

  // First paint at the travel offset (opacity 0), then flip to rest next frame so
  // the CSS transition animates it in — the area's standard entrance pattern.
  const entered = useMountAnimation();

  useEffect(() => {
    lastStepIndex = step;
  }, [step]);

  return (
    <Slide $dir={dirRef.current} $entered={entered}>
      {children}
    </Slide>
  );
};

export default DesktopStepSlide;
